import { Typography } from '@mui/material';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { Menu } from 'applications/GSP/layouts/MainLayout/components/Sidebar/components/components/Menu';
import { AnalyticsEvent } from 'applications/GSP/shared/constants/analytics';
import { RoutePath } from 'applications/GSP/shared/constants/router';
import { useAnalytics } from 'applications/GSP/shared/hooks/analytics';
import { useAppDispatch } from 'applications/GSP/shared/hooks/store';
import { studentActions } from 'applications/GSP/shared/store/student';
import { useMenu } from 'shared/components/Menu/hooks';
import { ArrowDropdownIcon } from 'shared/components/icons/ArrowDropdownIcon';

import { ContentWrapper } from './components/ContentWrapper';
import { EmptyState } from './components/EmptyState';
import { StudentAvatar } from './components/StudentAvatar';
import { Wrapper } from './components/Wrapper';
import {
  GRID_AREA_ARROW,
  GRID_AREA_GROUP,
  GRID_AREA_NAME,
  STUDENT_SECTION_TEST_ID,
} from './constants';
import {
  useReadOnly,
  useSelectableStudents,
  useStudentSectionUser,
} from './hooks';

import type { SidebarAnimatedComponentsState as StudentSectionProps } from 'applications/GSP/layouts/MainLayout/components/Sidebar/types';
import type { InspiredGlobalStudyPlatformApiModelsAccountStudent as Student } from 'applications/GSP/shared/api/gsp';
import type { FC } from 'react';

export const StudentSection: FC<StudentSectionProps> = ({ opened }) => {
  const dispatch = useAppDispatch();
  const { anchorEl, open, onClose, onOpen } = useMenu();
  const studentSectionUser = useStudentSectionUser();
  const readonly = useReadOnly();
  const selectableStudents = useSelectableStudents();
  const analytics = useAnalytics();
  const navigate = useNavigate();

  const onSelectStudent = useCallback(
    (student: Student) => {
      dispatch(studentActions.setStudent(student));
      onClose();
      analytics.track(AnalyticsEvent.SidebarStudentToggle);
      navigate(RoutePath.Dashboard);
    },
    [dispatch, onClose, analytics, navigate],
  );

  if (!studentSectionUser) return <EmptyState opened={opened} />;

  const { fullName, shortName, profilePictureUrl, subCaption } =
    studentSectionUser;

  return (
    <>
      <Wrapper
        data-testid={STUDENT_SECTION_TEST_ID}
        onClick={onOpen}
        opened={opened}
        readonly={readonly}
      >
        <StudentAvatar src={profilePictureUrl} alt={fullName} opened={opened}>
          {shortName}
        </StudentAvatar>
        <ContentWrapper opened={opened} readonly={readonly}>
          <Typography gridArea={GRID_AREA_NAME} noWrap maxWidth="100%">
            {fullName}
          </Typography>
          <Typography
            gridArea={GRID_AREA_GROUP}
            noWrap
            variant="body2"
            color="text.secondary"
            maxWidth="100%"
          >
            {subCaption}
          </Typography>
          {!readonly && (
            <ArrowDropdownIcon
              opened={open}
              sx={{ gridArea: GRID_AREA_ARROW }}
            />
          )}
        </ContentWrapper>
      </Wrapper>
      {!readonly && (
        <Menu
          open={open}
          anchorEl={anchorEl}
          onChange={onSelectStudent}
          onClose={onClose}
          students={selectableStudents}
        />
      )}
    </>
  );
};
